import type { Nullable } from "types/utilities";
import { create } from "zustand";

type AppleDepositStatus = "ready" | "success" | "processing";
type GoogleDepositStatus = "ready" | "success" | "processing";

type WalletStore = {
  appleDepositStatus: AppleDepositStatus;
  appleDepositError: Nullable<string>;
  googleDepositStatus: GoogleDepositStatus;
  googleDepositError: Nullable<string>;
  appGooglePayAvailable: boolean;
  appApplePayAvailable: boolean;
  isManual3ds: boolean;
  depositAmount: number;
  iomWithdrawalVerificationRequired: boolean;
};

type WalletActions = {
  setDepositAmount: (amount: number) => void;
  setAppleDepositStatus: (status: AppleDepositStatus) => void;
  setAppleDepositError: (error: Nullable<string>) => void;
  setGoogleDepositStatus: (status: GoogleDepositStatus) => void;
  setGoogleDepositError: (error: Nullable<string>) => void;
  setGooglePayAvailable: (available: boolean) => void;
  setApplePayAvailable: (available: boolean) => void;
  openManual3ds: () => void;
  closeManual3ds: () => void;
  setIomWithdrawalVerificationRequired: (required: boolean) => void;
  reset: () => void;
};

type WalletState = WalletStore & WalletActions;

const initialState: WalletStore = {
  appleDepositStatus: "ready",
  appleDepositError: null,
  googleDepositStatus: "ready",
  googleDepositError: null,
  appGooglePayAvailable: false,
  appApplePayAvailable: false,
  isManual3ds: false,
  depositAmount: 0,
  iomWithdrawalVerificationRequired: false,
};

const useWalletStore = create<WalletState>()((set) => ({
  ...initialState,

  setDepositAmount: (amount) => set({ depositAmount: amount }),

  setAppleDepositStatus: (status) => set({ appleDepositStatus: status }),

  setAppleDepositError: (error) => set({ appleDepositError: error }),

  setGoogleDepositStatus: (status) => set({ googleDepositStatus: status }),

  setGoogleDepositError: (error) => set({ googleDepositError: error }),

  setGooglePayAvailable: (available) =>
    set({ appGooglePayAvailable: available }),

  setApplePayAvailable: (available) => set({ appApplePayAvailable: available }),

  openManual3ds: () => set({ isManual3ds: true }),

  closeManual3ds: () => set({ isManual3ds: false }),

  setIomWithdrawalVerificationRequired: (required) =>
    set({ iomWithdrawalVerificationRequired: required }),

  reset: () => set(initialState),
}));

export { useWalletStore };
export type {
  WalletStore,
  WalletActions,
  WalletState,
  AppleDepositStatus,
  GoogleDepositStatus,
};
